import { createLogger } from '@svenskaspelab/nova-core/log';

import type { Definition } from '../../../../../schema/frameworks/entry.js';
import type { SystemType } from '../../../../../schema/system/entry.js';
import { BadRequestFileUploadError } from '../../../../domain/entry.js';
import type { parseBetPropertyData } from './parseBetPropertyData.js';

type ExternaSystemType = Extract<SystemType, 'MSYS' | 'SROW'>;
type SystemSize = number;

const logger = createLogger('poolbetting.file-upload');

function parseSystemColumn(system: string, lineNumber: number): [ExternaSystemType, SystemSize] {
  const currentSystem = system.charAt(0);

  if (currentSystem === 'E') {
    return ['SROW', 1];
  }

  if (currentSystem === 'M') {
    const systemSize = Number.parseInt(system.slice(1), 10);
    if (Number.isNaN(systemSize)) {
      throw new BadRequestFileUploadError(
        `Missing system size for MSYS, line: ${lineNumber}`,
        undefined,
        'CONTENT_SYSTEM_SIZE_MISSING'
      );
    }

    return ['MSYS', systemSize];
  }

  throw new BadRequestFileUploadError(
    `Unsupported system: ${currentSystem}, line: ${lineNumber}`,
    undefined,
    'CONTENT_SYSTEM_UNSUPPORTED'
  );
}

type OnLineCompleteCallback = (
  systemInfo: { systemType: ExternaSystemType; systemSize: number },
  columns: string[]
) => void;

export type ParseSystemReturn = { systemType: ExternaSystemType; items: string[] };

function parseSystemLines(lines: string[], onLineComplete?: OnLineCompleteCallback): ParseSystemReturn {
  const items: string[] = [];
  let fileSystemType: ExternaSystemType | undefined = undefined;
  let lineNumber = 0;

  for (const line of lines) {
    lineNumber++;
    const [systemColumn, ...columns] = line.trim().toUpperCase().split(',');

    if (!systemColumn) {
      throw new BadRequestFileUploadError(`No system found for line: ${line}`, undefined, 'CONTENT_SYSTEM_MISSING');
    }

    const [systemType, systemSize] = parseSystemColumn(systemColumn, lineNumber);

    if (fileSystemType !== undefined && fileSystemType !== systemType) {
      throw new BadRequestFileUploadError(
        'Inconsistent systems found in file',
        undefined,
        'CONTENT_SYSTEM_INCONSISTENT'
      );
    }

    fileSystemType = systemType;

    onLineComplete?.({ systemType, systemSize }, columns);

    items.push(columns.join(','));
  }

  if (!fileSystemType) {
    throw new BadRequestFileUploadError('No system found in file', undefined, 'CONTENT_SYSTEM_MISSING');
  }

  return {
    systemType: fileSystemType,
    items,
  };
}

/**
 * Parse external system for tipsen products.
 *
 * @param lines lines to parse
 * @param product Type of product
 * @returns system information
 * @throws {BadRequestFileUploadError} if the system is invalid or inconsistent across lines
 */
function parseTipsenSystem(
  lines: string[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  product: Definition['drawProducts'][number]
): ReturnType<typeof parseSystemLines> {
  return parseSystemLines(lines, ({ systemType, systemSize }, columns) => {
    if (systemType === 'MSYS') {
      const expectedSystemSize = columns.reduce((size, cell) => size * cell.length, 1);

      if (expectedSystemSize !== systemSize) {
        throw new BadRequestFileUploadError(
          `Expected system size ${expectedSystemSize} doesn't match the given system size ${systemSize}`,
          undefined,
          'CONTENT_SYSTEM_SIZE_UNEXPECTED'
        );
      }
    }
  });
}

/**
 * Parse external system for scoreboard products.
 *
 * @param lines lines to parse
 * @param product Type of product
 * @returns system information
 * @throws {BadRequestFileUploadError} if the system is invalid or inconsistent across lines
 */
function parseScoreboardSystem(
  lines: string[],
  product: Definition['drawProducts'][number]
): ReturnType<typeof parseSystemLines> {
  return parseSystemLines(lines, ({ systemType }) => {
    if (product === 'Bomben' && systemType === 'MSYS') {
      throw new BadRequestFileUploadError(`MSYS Not supported for ${product}`, undefined, 'CONTENT_SYSTEM_UNSUPPORTED');
    }
  });
}

/**
 * Parse system line for external system.
 *
 * @param product Type of product
 * @param lines lines to parse
 * @returns system information
 * @throws {BadRequestFileUploadError}
 */
export function parseSystem(
  product: ReturnType<typeof parseBetPropertyData>['product'],
  lines: string[]
): ParseSystemReturn {
  switch (product) {
    case 'Topptipset':
    case 'TopptipsetStryk':
    case 'TopptipsetExtra':
    case 'Powerplay':
    case 'Maltipset':
    case 'Stryktipset':
    case 'Europatipset':
      return parseTipsenSystem(lines, product);
    case 'Bomben':
      return parseScoreboardSystem(lines, product);
    default:
      logger.info(`Product '${product}' not supported for file uploads`);
      throw new BadRequestFileUploadError(
        `Product '${product}' not supported for file uploads`,
        undefined,
        'PRODUCT_NOT_SUPPORTED'
      );
  }
}
